import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { PermissionsQuery } from '#types/generated/graphql';
import CommonPermissions from './CommonPermissions';
import { useInstitution } from '#core/Institution';

const Permissions: React.FC<{}> = ({ children }) => {
  const { institution } = useInstitution();

  const { loading, error, data, refetch } = useQuery<PermissionsQuery>(
    gql`
      query Permissions($institutionId: String!) {
        permissions(institutionId: $institutionId) {
          resource
          actions {
            action
            grant
          }
        }
      }
    `,
    {
      variables: {
        institutionId: institution.institutionId,
      },
    },
  );

  const resourcePermissions = useMemo(
    () =>
      (data?.permissions || []).reduce((perms, permission) => {
        if (!permission) {
          return perms;
        }
        const { resource, actions } = permission;
        return {
          ...perms,
          [resource]: actions.reduce((a, resourceActions) => {
            if (!resourceActions) {
              return a;
            }
            const { action, grant } = resourceActions;
            return {
              ...a,
              [action]: grant,
            };
          }, {} as Record<string, boolean>),
        };
      }, {} as Record<string, Record<string, boolean>>),
    [data?.permissions],
  );

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  return (
    <CommonPermissions
      resourcePermissions={resourcePermissions}
      refetchPermissions={refetch}
    >
      {children}
    </CommonPermissions>
  );
};

export default Permissions;
